import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaFileInvoiceDollar, FaMoneyBillWave, FaChartBar, FaEdit, FaTrash, FaPlus, FaEye, FaFilePdf, FaSearch, FaCalendarAlt, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import axios from '../api/axios';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const StatCard = ({ title, value, icon, color }) => (
  <div className={`bg-gradient-to-r ${color} text-white p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}>
    <div className="flex justify-between items-center">
      <div>
        <p className="text-sm">{title}</p>
        <h3 className="text-3xl font-bold">{value}</h3>
      </div>
      <div className="text-4xl opacity-80">{icon}</div>
    </div>
  </div>
);

export default function FacturasTable() {
  const [facturas, setFacturas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('fechaFactura');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchFacturas();
  }, []);

  const fetchFacturas = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/facturas');
      setFacturas(response.data);
    } catch (err) {
      setError('Error al cargar las facturas. Por favor, intente de nuevo.');
      console.error('Error fetching facturas:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredFacturas = useMemo(() => {
    return facturas.filter(factura => {
      const matchesSearch =
        factura.tercero.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        factura.tercero.documento.toLowerCase().includes(searchTerm.toLowerCase()) ||
        factura.codigo.toLowerCase().includes(searchTerm.toLowerCase());

      const facturaDate = new Date(factura.fechaFactura);
      const matchesDateRange =
        (!startDate || facturaDate >= startDate) &&
        (!endDate || facturaDate <= endDate);

      return matchesSearch && matchesDateRange;
    });
  }, [facturas, searchTerm, startDate, endDate]);

  const sortedFacturas = useMemo(() => {
    return [...filteredFacturas].sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredFacturas, sortColumn, sortDirection]);

  const handleDeleteFactura = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta factura?')) {
      try {
        await axios.delete(`/facturas/${id}`);
        fetchFacturas();
      } catch (err) {
        setError('Error al eliminar la factura. Por favor, intente de nuevo.');
        console.error('Error deleting factura:', err);
      }
    }
  };

  const handleExportPDF = async (id) => {
    try {
      const response = await axios.get(`/facturas/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `factura_${id}.pdf`;
      link.click();
    } catch (err) {
      setError('Error al exportar la factura a PDF. Por favor, intente de nuevo.');
      console.error('Error exporting factura to PDF:', err);
    }
  };

  const handleSort = (column) => {
    setSortDirection(sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center mt-8 text-red-500">{error}</div>;
  }

  const totalFacturas = sortedFacturas.length;
  const totalIngresos = sortedFacturas.reduce((sum, factura) => sum + (parseFloat(factura.totalConIva) || 0), 0);
  const promedioFactura = totalFacturas > 0 ? totalIngresos / totalFacturas : 0;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedFacturas.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center text-gray-800 font-serif">Gestión de Facturas</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard title="Total Facturas" value={totalFacturas} icon={<FaFileInvoiceDollar />} color="from-blue-500 to-blue-700" />
        <StatCard title="Ingresos Totales" value={`${totalIngresos.toFixed(2)} €`} icon={<FaMoneyBillWave />} color="from-green-500 to-green-700" />
        <StatCard title="Promedio por Factura" value={`${promedioFactura.toFixed(2)} €`} icon={<FaChartBar />} color="from-yellow-500 to-orange-600" />
      </div>
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Facturas</h2>
          <Link to="/home/facturas/add" className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-900 transition-colors duration-200 flex items-center">
            <FaPlus className="mr-2" /> Nueva Factura
          </Link>
        </div>
        <div className="p-4 flex flex-wrap justify-between items-center bg-gray-100">
          <div className="flex items-center mb-2 md:mb-0">
            <FaSearch className="text-blue-700 mr-2" />
            <input
              type="text"
              placeholder="Buscar por Código, Nombre o Documento"
              className="border p-2 rounded w-80"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center mb-2 md:mb-0">
            <FaCalendarAlt className="text-green-700 mr-2" />
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              placeholderText="Fecha inicio"
              className="border p-2 rounded mr-2"
              dateFormat="dd/MM/yyyy"
            />
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              placeholderText="Fecha fin"
              className="border p-2 rounded"
              dateFormat="dd/MM/yyyy"
            />
          </div>
          <div>
            <label htmlFor="itemsPerPage" className="mr-2">Mostrar:</label>
            <select
              id="itemsPerPage"
              className="border p-2 rounded"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={sortedFacturas.length}>Todos</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('codigo')}>
                  Código {sortColumn === 'codigo' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('fechaFactura')}>
                  Fecha Factura {sortColumn === 'fechaFactura' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tercero</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documento</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('totalConIva')}>
                  Total {sortColumn === 'totalConIva' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map(factura => (
                <tr key={factura.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">{factura.codigo}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{new Date(factura.fechaFactura).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{factura.tercero.nombre}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{factura.tercero.documento}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{factura.descripcion}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{parseFloat(factura.totalConIva).toFixed(2)} €</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <Link to={`/home/facturas/view/${factura.id}`} className="text-blue-600 hover:text-blue-900">
                        <FaEye className="h-5 w-5" />
                      </Link>
                      <Link to={`/home/facturas/edit/${factura.id}`} className="text-green-600 hover:text-green-900">
                        <FaEdit className="h-5 w-5" />
                      </Link>
                      <button onClick={() => handleDeleteFactura(factura.id)} className="text-red-600 hover:text-red-900">
                        <FaTrash className="h-5 w-5" />
                      </button>
                      <button onClick={() => handleExportPDF(factura.id)} className="text-orange-600 hover:text-orange-900">
                        <FaFilePdf className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 flex justify-between items-center bg-gray-50">
          <div>
            Mostrando {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, sortedFacturas.length)} de {sortedFacturas.length} facturas
          </div>
          <div>
            {Array.from({ length: Math.ceil(sortedFacturas.length / itemsPerPage) }, (_, i) => (
              <button
                key={i}
                onClick={() => paginate(i + 1)}
                className={`mx-1 px-3 py-1 rounded ${currentPage === i + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
                  }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}